import React from "react";
import { Button as MuiButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { colors } from "../../config/colors";

// Styled MUI Button variants
const StyledPrimaryButton = styled(MuiButton)(({ theme }) => ({
  background: `linear-gradient(to right, ${colors.primary[500]}, ${colors.primary[600]})`,
  color: "white",
  fontWeight: "bold",
  padding: "10px 24px",
  borderRadius: "12px",
  fontSize: "16px",
  textTransform: "none",
  boxShadow:
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: `linear-gradient(to right, ${colors.primary[600]}, ${colors.primary[700]})`,
    boxShadow:
      "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    transform: "translateY(-4px)",
  },
  "&:disabled, &.Mui-disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
    transform: "none",
    background: colors.gray[300],
    color: colors.gray[500],
    boxShadow: "none",
  },
}));

const StyledSecondaryButton = styled(MuiButton)(({ theme }) => ({
  background: "white",
  color: colors.primary[500],
  border: `2px solid ${colors.primary[500]}`,
  fontWeight: "bold",
  padding: "10px 24px",
  borderRadius: "12px",
  fontSize: "16px",
  textTransform: "none",
  boxShadow:
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: colors.primary[50],
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  },
  "&:disabled, &.Mui-disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
    borderColor: colors.gray[200],
    color: colors.gray[400],
  },
}));

const StyledGradientButton = styled(MuiButton)(({ theme }) => ({
  background: `linear-gradient(to right, ${colors.primary[500]}, ${colors.primary[600]}, ${colors.secondary[500]})`,
  color: "white",
  fontWeight: "bold",
  padding: "12px 32px",
  borderRadius: "16px",
  fontSize: "18px",
  textTransform: "none",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 30px 60px -12px rgba(0, 0, 0, 0.35)",
    transform: "translateY(-4px)",
  },
  "&:disabled, &.Mui-disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
    transform: "none",
    background: colors.gray[400],
    color: colors.gray[500],
  },
}));

const StyledIconButton = styled(MuiButton)(({ theme }) => ({
  minWidth: "48px",
  width: "48px",
  height: 35,
  borderRadius: "50%",
  padding: 0,
  background: "white",
  border: `2px solid ${colors.gray[200]}`,
  boxShadow:
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: colors.primary[50],
    borderColor: colors.primary[500],
    boxShadow:
      "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  },
  "&:disabled, &.Mui-disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
    borderColor: colors.gray[300],
    color: colors.gray[300],
  },
}));

const StyledSmallButton = styled(MuiButton)(({ theme }) => ({
  background: `linear-gradient(to right, ${colors.primary[500]}, ${colors.primary[600]})`,
  color: "white",
  fontWeight: "bold",
  padding: "8px 20px",
  borderRadius: "12px",
  fontSize: "14px",
  textTransform: "none",
  boxShadow:
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: `linear-gradient(to right, ${colors.primary[600]}, ${colors.primary[700]})`,
    boxShadow:
      "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    transform: "scale(1.05)",
  },
  "&:disabled, &.Mui-disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
    transform: "none",
    background: colors.gray[300],
    color: colors.gray[500],
  },
}));

const StyledGhostButton = styled(MuiButton)(({ theme }) => ({
  background: "transparent",
  color: "#374151",
  fontWeight: "bold",
  padding: "8px 16px",
  borderRadius: "8px",
  fontSize: "14px",
  textTransform: "none",
  transition: "all 0.2s ease",
  "&:hover": {
    background: colors.primary[50],
    color: colors.primary[600],
  },
  "&:disabled, &.Mui-disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
    color: colors.gray[300],
  },
}));

const Button = ({
  children,
  variant = "primary",
  size = "sm",
  icon,
  iconPosition = "right",
  iconFlip = false,
  onClick,
  className = "",
  type = "button",
  disabled = false,
  ...props
}) => {
  // Icon component
  const IconComponent = icon && (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      {typeof icon === "string" ? (
        <img
          src={icon}
          alt="icon"
          style={{
            width: variant === "icon" ? "24px" : "20px",
            height: variant === "icon" ? "24px" : 35,
            filter:
              variant === "primary" || variant === "gradient"
                ? "brightness(0) invert(1)"
                : iconFlip
                  ? "scaleX(-1)"
                  : "none",
            transform:
              iconFlip && variant !== "primary" && variant !== "gradient"
                ? "scaleX(-1)"
                : "none",
          }}
        />
      ) : (
        icon
      )}
    </span>
  );

  // Select the appropriate styled component
  let StyledButton;
  if (variant === "icon") {
    StyledButton = StyledIconButton;
  } else if (variant === "ghost") {
    StyledButton = StyledGhostButton;
  } else if (size === "sm") {
    StyledButton = StyledSmallButton;
  } else if (variant === "secondary") {
    StyledButton = StyledSecondaryButton;
  } else if (variant === "gradient") {
    StyledButton = StyledGradientButton;
  } else {
    StyledButton = StyledPrimaryButton;
  }

  // Content arrangement
  const content =
    variant === "icon" ? (
      IconComponent || children
    ) : (
      <>
        {iconPosition === "left" && IconComponent}
        {children}
        {iconPosition === "right" && IconComponent}
      </>
    );

  return (
    <StyledButton
      {...props}
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={className}
      sx={{
        ...(props.sx || {}),
        px: { xs: 3, "2xl": 4, "3xl": 6, "4xl": 5 },
        py: { xs: 1.5, "2xl": 2, "3xl": 2, "4xl": 4 },
        fontSize: {
         xs: "0.895rem", "2xl": "0.999rem", "3xl": "1.1rem", "4xl": "1.3rem"
        },
        height: { xs: 40, "2xl": 48, "3xl": 50, "4xl": 55 },
        borderRadius: {
          xs: "12px",
          "2xl": "14px",
          "3xl": "18px",
          "4xl": "24px",
        },
      }}
    >
      {content}
    </StyledButton>
  );
};

export default Button;
